// src/modules/genai/GANLab.jsx
import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const P = ['#f87171','#22d3ee','#4ade80','#a78bfa','#fbbf24','#fb923c'];
const Btn = ({onClick,children,color='#f87171',disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=0.01,onChange,unit='',color='#f87171'}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))} style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);
const Sel = ({label,value,onChange,options}) => (
  <div style={{marginBottom:'8px'}}>
    {label&&<div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'3px'}}>{label}</div>}
    <select value={value} onChange={e=>onChange(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'6px 8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none'}}>
      {options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
    </select>
  </div>
);

const randn = () => { let u=0,v=0; while(!u)u=Math.random(); while(!v)v=Math.random(); return Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v); };

const simGAN = ({epochs, lrG, lrD, ganType, dataMode}) => {
  const pts=[]; let dLoss=1.386,gLoss=1.386,dReal=0.5,dFake=0.5,modeCollapse=false;
  const boost={vanilla:0.8,dcgan:1.0,wgan:1.2,stylegan:1.15}[ganType]||1;
  const forgetRisk={vanilla:0.25,dcgan:0.1,wgan:0.03,stylegan:0.05}[ganType]||0.1;
  for(let e=0;e<epochs;e++){
    if(e>30&&!modeCollapse&&Math.random()<forgetRisk*0.01*(lrG/lrD>3?3:1)) modeCollapse=true;
    const n=randn()*0.04;
    if(modeCollapse){
      dFake=Math.min(0.85,dFake+0.003+n*0.3); dReal=Math.max(0.4,dReal-0.001);
      gLoss=Math.max(0.1,gLoss*(1-0.001));
    } else {
      dReal=Math.min(0.97,dReal+lrD*boost*(0.85-dReal)+n*0.3);
      dFake=Math.max(0.03,dFake+lrD*boost*(0.15-dFake)+n*0.3);
      gLoss=Math.max(0.01,-Math.log(dFake+1e-8)*0.85+randn()*0.06);
    }
    dLoss=Math.max(0.01,-(Math.log(dReal+1e-8)+Math.log(1-dFake+1e-8))+randn()*0.04);
    if(e%Math.max(1,Math.floor(epochs/80))===0) pts.push({epoch:e,dLoss:+dLoss.toFixed(4),gLoss:+gLoss.toFixed(4),dReal:+dReal.toFixed(4),dFake:+dFake.toFixed(4)});
  }
  return {pts,modeCollapse};
};

const genPts = (n, mode, offset=0) => Array.from({length:n},()=>{
  if(mode==='ring'){const a=Math.random()*2*Math.PI,r=1+randn()*0.12;return{x:r*Math.cos(a),y:r*Math.sin(a)};}
  if(mode==='grid'){return{x:Math.round(randn()*1.5)*1.2+randn()*0.15,y:Math.round(randn()*1.5)*1.2+randn()*0.15};}
  if(mode==='collapse'){return{x:0.5+randn()*0.08+Math.sin(offset)*0.05,y:0.5+randn()*0.08};}
  return{x:randn()*0.6+Math.cos(offset*0.05)*0.1,y:randn()*0.6+Math.sin(offset*0.05)*0.1};
});

function Scatter({real,fake,title,W=300,H=230}) {
  const all=[...real,...fake],pad=24;
  const mnX=Math.min(...all.map(p=>p.x))-0.3,mxX=Math.max(...all.map(p=>p.x))+0.3;
  const mnY=Math.min(...all.map(p=>p.y))-0.3,mxY=Math.max(...all.map(p=>p.y))+0.3;
  const sx=x=>(x-mnX)/(mxX-mnX)*(W-pad*2)+pad;
  const sy=y=>(1-(y-mnY)/(mxY-mnY))*(H-pad*2)+pad;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'6px',border:'1px solid #1e293b',display:'block'}}>
      <text x={W/2} y={13} textAnchor="middle" style={{fontSize:'8px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>{title}</text>
      {real.map((p,i)=><circle key={`r${i}`} cx={sx(p.x)} cy={sy(p.y)} r={3.5} fill="#22d3ee" opacity={0.75}/>)}
      {fake.map((p,i)=><circle key={`f${i}`} cx={sx(p.x)} cy={sy(p.y)} r={3.5} fill="#f87171" opacity={0.75}/>)}
      <circle cx={pad} cy={H-10} r={4} fill="#22d3ee"/><text x={pad+8} y={H-7} style={{fontSize:'7px',fill:'#22d3ee',fontFamily:'JetBrains Mono'}}>real</text>
      <circle cx={pad+42} cy={H-10} r={4} fill="#f87171"/><text x={pad+50} y={H-7} style={{fontSize:'7px',fill:'#f87171',fontFamily:'JetBrains Mono'}}>fake</text>
    </svg>
  );
}

function ArchSVG({dScore, gPulse}) {
  const W=660,H=150;
  const pulse = 0.08+Math.abs(Math.sin(gPulse*0.4))*0.12;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
      {/* Noise */}
      <rect x={10} y={45} width={90} height={60} rx={4} fill="#a78bfa18" stroke="#a78bfa" strokeWidth={1.5}/>
      <text x={55} y={71} textAnchor="middle" style={{fontSize:'9px',fill:'#a78bfa',fontFamily:'JetBrains Mono',fontWeight:700}}>NOISE z</text>
      <text x={55} y={85} textAnchor="middle" style={{fontSize:'8px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>latent space</text>
      {/* arrow */}
      <line x1={100} y1={75} x2={140} y2={75} stroke="#a78bfa66" strokeWidth={2}/>
      <polygon points="140,71 148,75 140,79" fill="#a78bfa66"/>
      {/* Generator */}
      <rect x={148} y={25} width={130} height={100} rx={4} fill={`rgba(248,113,113,${pulse})`} stroke="#f87171" strokeWidth={1.5}/>
      <text x={213} y={68} textAnchor="middle" style={{fontSize:'11px',fill:'#f87171',fontFamily:'JetBrains Mono',fontWeight:700}}>GENERATOR</text>
      <text x={213} y={83} textAnchor="middle" style={{fontSize:'8px',fill:'#94a3b8',fontFamily:'JetBrains Mono'}}>G(z) → fake x</text>
      <text x={213} y={96} textAnchor="middle" style={{fontSize:'8px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>Conv / FC layers</text>
      {/* arrow */}
      <line x1={278} y1={75} x2={318} y2={75} stroke="#f8717166" strokeWidth={2}/>
      <polygon points="318,71 326,75 318,79" fill="#f8717166"/>
      {/* Fake node */}
      <rect x={326} y={56} width={80} height={38} rx={4} fill="#f8717115" stroke="#f87171" strokeWidth={1}/>
      <text x={366} y={73} textAnchor="middle" style={{fontSize:'9px',fill:'#f87171',fontFamily:'JetBrains Mono',fontWeight:700}}>FAKE x</text>
      <text x={366} y={86} textAnchor="middle" style={{fontSize:'8px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>G(z)</text>
      {/* Real data from top */}
      <rect x={326} y={8} width={80} height={30} rx={4} fill="#22d3ee15" stroke="#22d3ee" strokeWidth={1}/>
      <text x={366} y={20} textAnchor="middle" style={{fontSize:'8px',fill:'#22d3ee',fontFamily:'JetBrains Mono',fontWeight:700}}>REAL DATA</text>
      <text x={366} y={31} textAnchor="middle" style={{fontSize:'7px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>training set</text>
      <line x1={366} y1={38} x2={420} y2={65} stroke="#22d3ee44" strokeWidth={1.5}/>
      {/* arrow from fake */}
      <line x1={406} y1={75} x2={446} y2={75} stroke="#f8717166" strokeWidth={2}/>
      <polygon points="446,71 454,75 446,79" fill="#64748b"/>
      {/* Discriminator */}
      <rect x={454} y={25} width={150} height={100} rx={4} fill={`rgba(34,211,238,${0.03+dScore*0.12})`} stroke="#22d3ee" strokeWidth={1.5}/>
      <text x={529} y={65} textAnchor="middle" style={{fontSize:'10px',fill:'#22d3ee',fontFamily:'JetBrains Mono',fontWeight:700}}>DISCRIMINATOR</text>
      <text x={529} y={78} textAnchor="middle" style={{fontSize:'8px',fill:'#94a3b8',fontFamily:'JetBrains Mono'}}>real or fake?</text>
      <text x={529} y={100} textAnchor="middle" style={{fontSize:'13px',fill:'#fbbf24',fontFamily:'JetBrains Mono',fontWeight:700}}>D(x)={dScore.toFixed(2)}</text>
      {/* G loss feedback */}
      <path d={`M600 135 Q600 148 213 148 Q148 148 148 125`} fill="none" stroke="#f87171" strokeWidth={1.5} strokeDasharray="6 3"/>
      <text x={380} y={145} textAnchor="middle" style={{fontSize:'8px',fill:'#f87171',fontFamily:'JetBrains Mono'}}>G loss ← fool D</text>
    </svg>
  );
}

const TABS=['architecture','training','latent-walk','mode-collapse','variants'];

export default function GANLab() {
  const [tab,setTab]=useState('architecture');
  const [logs,setLogs]=useState([{ts:'00:00:00',msg:'GAN Lab — Generator vs Discriminator. The original generative AI.',t:'info'}]);
  const log=(msg,t='info')=>setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);
  const [lrG,setLrG]=useState(0.0002);
  const [lrD,setLrD]=useState(0.0002);
  const [ganType,setGanType]=useState('dcgan');
  const [epochs,setEpochs]=useState(200);
  const [dataMode,setDataMode]=useState('gaussian');
  const [result,setResult]=useState(null);
  const [running,setRunning]=useState(false);
  const [walkStep,setWalkStep]=useState(0);
  const [walking,setWalking]=useState(false);
  const walkRef=useRef();

  const run=()=>{setRunning(true);setTimeout(()=>{const r=simGAN({epochs,lrG,lrD,ganType,dataMode});setResult(r);log(`${ganType.toUpperCase()} done — collapse:${r.modeCollapse} | D(real)=${r.pts.at(-1)?.dReal} D(fake)=${r.pts.at(-1)?.dFake}`,'ok');setRunning(false);},30);};
  const toggleWalk=()=>{if(walking){clearInterval(walkRef.current);setWalking(false);}else{setWalking(true);walkRef.current=setInterval(()=>setWalkStep(s=>s+1),220);}};
  useEffect(()=>()=>clearInterval(walkRef.current),[]);

  const last=result?.pts.at(-1);
  const realPts=genPts(40,dataMode);

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?'#f87171':'transparent'}`,background:tab===t?'#f8717118':'transparent',color:tab===t?'#f87171':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr 1fr auto',gap:'10px',padding:'12px',background:'var(--surface)',border:'1px solid var(--b0)',borderRadius:'4px',alignItems:'end'}}>
        <Sel label="GAN TYPE" value={ganType} onChange={setGanType} options={[{value:'vanilla',label:'Vanilla GAN'},{value:'dcgan',label:'DCGAN'},{value:'wgan',label:'WGAN'},{value:'stylegan',label:'StyleGAN (sim)'}]}/>
        <Sel label="DATA SHAPE" value={dataMode} onChange={setDataMode} options={[{value:'gaussian',label:'Gaussian'},{value:'ring',label:'Ring'},{value:'grid',label:'Grid'}]}/>
        <Sl label="LR Generator" value={lrG} min={0.00001} max={0.001} step={0.00001} onChange={setLrG} color="#f87171"/>
        <Sl label="LR Discriminator" value={lrD} min={0.00001} max={0.001} step={0.00001} onChange={setLrD} color="#22d3ee"/>
        <Sl label="Epochs" value={epochs} min={50} max={500} step={10} onChange={setEpochs} color="#fbbf24"/>
        <Btn onClick={run} disabled={running} color="#f87171">{running?'TRAINING…':'TRAIN →'}</Btn>
      </div>

      {tab==='architecture'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="GAN ARCHITECTURE — ADVERSARIAL GAME" accent="#f87171">
            <ArchSVG dScore={last?.dFake??0.5} gPulse={walkStep}/>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            <Panel title="GENERATOR G" accent="#f87171">
              {[['Input','Latent z ~ N(0,I)','#a78bfa'],['Goal','Produce x so real D(G(z)) → 1','#f87171'],['Loss','min −log D(G(z))','#fbbf24'],['Arch','FC/Conv → BatchNorm → ReLU','#64748b'],['Never sees','Real data directly','#64748b']].map(([k,v,c])=>(
                <div key={k} style={{marginBottom:'5px',padding:'5px 8px',background:'var(--deep)',borderRadius:'2px',borderLeft:`2px solid ${c}`,fontSize:'9px',display:'flex',gap:'8px'}}>
                  <span style={{color:'var(--t3)',minWidth:60}}>{k}</span><span style={{color:c,fontFamily:'JetBrains Mono'}}>{v}</span>
                </div>
              ))}
            </Panel>
            <Panel title="DISCRIMINATOR D" accent="#22d3ee">
              {[['Input','Real OR fake sample x','#22d3ee'],['Goal','D(real)→1, D(G(z))→0','#22d3ee'],['Loss','min −[log D(x) + log(1−D(G(z)))]','#fbbf24'],['Arch','Conv/FC → LeakyReLU → Sigmoid','#64748b'],['Output','P(real) ∈ [0,1]','#4ade80']].map(([k,v,c])=>(
                <div key={k} style={{marginBottom:'5px',padding:'5px 8px',background:'var(--deep)',borderRadius:'2px',borderLeft:`2px solid ${c}`,fontSize:'9px',display:'flex',gap:'8px'}}>
                  <span style={{color:'var(--t3)',minWidth:60}}>{k}</span><span style={{color:c,fontFamily:'JetBrains Mono'}}>{v}</span>
                </div>
              ))}
            </Panel>
          </div>
        </div>
      )}

      {tab==='training'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          {result?(<>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
              <Metric label="D Loss (final)" value={last?.dLoss} color="#22d3ee"/>
              <Metric label="G Loss (final)" value={last?.gLoss} color="#f87171"/>
              <Metric label="D(real)" value={last?.dReal} color="#4ade80"/>
              <Metric label="D(fake)" value={last?.dFake} color="#fbbf24"/>
            </div>
            <Panel title="LOSS CURVES — ADVERSARIAL EQUILIBRIUM" accent="#f87171">
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>Nash equilibrium: both losses near log(2)≈0.693. G fools D 50% of the time.</div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={result.pts}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                  <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                  <Tooltip/><Legend/>
                  <ReferenceLine y={0.693} stroke="#fbbf24" strokeDasharray="5 3" label={{value:'Nash eq.',fill:'#fbbf24',fontSize:8}}/>
                  <Line type="monotone" dataKey="dLoss" stroke="#22d3ee" strokeWidth={2} dot={false} name="D Loss"/>
                  <Line type="monotone" dataKey="gLoss" stroke="#f87171" strokeWidth={2} dot={false} name="G Loss"/>
                </LineChart>
              </ResponsiveContainer>
            </Panel>
            <Panel title="DISCRIMINATOR CONFIDENCE — D(real) vs D(fake)" accent="#4ade80">
              <ResponsiveContainer width="100%" height={160}>
                <AreaChart data={result.pts}>
                  <defs>
                    <linearGradient id="gr" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#4ade80" stopOpacity={0.4}/><stop offset="95%" stopColor="#4ade80" stopOpacity={0}/></linearGradient>
                    <linearGradient id="gf" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f87171" stopOpacity={0.4}/><stop offset="95%" stopColor="#f87171" stopOpacity={0}/></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/><YAxis domain={[0,1]} tick={{fontSize:9,fill:'var(--t3)'}}/>
                  <Tooltip/><Legend/>
                  <ReferenceLine y={0.5} stroke="#fbbf24" strokeDasharray="3 2"/>
                  <Area type="monotone" dataKey="dReal" stroke="#4ade80" fill="url(#gr)" strokeWidth={2} name="D(real)"/>
                  <Area type="monotone" dataKey="dFake" stroke="#f87171" fill="url(#gf)" strokeWidth={2} name="D(fake)"/>
                </AreaChart>
              </ResponsiveContainer>
            </Panel>
          </>):<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Train the GAN first</div>}
          <LogPanel logs={logs} height={70}/>
        </div>
      )}

      {tab==='latent-walk'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="LATENT SPACE WALK — INTERPOLATING z → SMOOTH OUTPUT TRANSITIONS" accent="#a78bfa">
            <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'10px'}}>Linear interpolation through z-space: z_t = z₁ + t(z₂-z₁). Each step = different z vector fed to G.</div>
            <div style={{display:'flex',gap:'12px',overflowX:'auto',padding:'8px 0'}}>
              {Array.from({length:6},(_,i)=>(
                <Scatter key={i} real={realPts.slice(0,20)} fake={genPts(30,dataMode,walkStep+i*15)} title={`z step ${i+1}`} W={210} H={190}/>
              ))}
            </div>
            <Btn onClick={toggleWalk} color={walking?'#f87171':'#a78bfa'} style={{marginTop:'8px'}}>{walking?'STOP':'ANIMATE WALK →'}</Btn>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
            {[
              {t:'Interpolation',d:'z₁→z₂ linear path → smooth morphing between two generated samples. No abrupt jumps.',c:'#a78bfa'},
              {t:'Vector Arithmetic',d:'"king" − "man" + "woman" = "queen". Semantic meaning encoded in z dimensions.',c:'#22d3ee'},
              {t:'Disentanglement',d:'Ideal: each z dim controls one attribute (pose, lighting, age). VAE-GAN approaches this.',c:'#4ade80'},
            ].map(({t,d,c})=>(
              <Panel key={t} title={t} accent={c}>
                <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.7}}>{d}</div>
              </Panel>
            ))}
          </div>
        </div>
      )}

      {tab==='mode-collapse'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            <Panel title="HEALTHY GAN — DIVERSE SAMPLES" accent="#4ade80">
              <Scatter real={realPts} fake={genPts(40,dataMode,0)} title="Fake samples covering full distribution" W={340} H={240}/>
            </Panel>
            <Panel title="MODE COLLAPSE — STUCK ON ONE MODE" accent="#f87171">
              <Scatter real={realPts} fake={genPts(40,'collapse',0)} title="All fakes cluster at one point!" W={340} H={240}/>
            </Panel>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            <Panel title="WHY IT HAPPENS" accent="#f87171">
              {['G exploits one easy fooling strategy','D never adapts to the single mode G uses','No gradient signal to explore other modes','High lrG / low lrD imbalance','Vanilla BCE loss allows G to "win" locally'].map((t,i)=>(
                <div key={i} style={{fontSize:'9px',padding:'5px 8px',background:'var(--deep)',borderRadius:'2px',marginBottom:'4px',borderLeft:'2px solid #f87171',color:'var(--t2)'}}>⚠ {t}</div>
              ))}
            </Panel>
            <Panel title="HOW TO FIX" accent="#4ade80">
              {['WGAN: Wasserstein loss penalizes collapse','Minibatch discrimination: D sees diversity','Feature matching: match real/fake statistics','Spectral normalization: stabilize D gradients','Balance lrG ≈ lrD or use lrD slightly higher'].map((t,i)=>(
                <div key={i} style={{fontSize:'9px',padding:'5px 8px',background:'var(--deep)',borderRadius:'2px',marginBottom:'4px',borderLeft:'2px solid #4ade80',color:'var(--t2)'}}>✓ {t}</div>
              ))}
            </Panel>
          </div>
        </div>
      )}

      {tab==='variants'&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
          {[
            {name:'Vanilla GAN',year:'2014',loss:'BCE',good:'Elegant minimax concept',bad:'Vanishing gradients, unstable',use:'Academic baseline',c:'#f87171'},
            {name:'DCGAN',year:'2015',loss:'BCE',good:'Conv + BN = stable images',bad:'Mode collapse still possible',use:'Image generation baseline',c:'#fb923c'},
            {name:'WGAN',year:'2017',loss:'Wasserstein',good:'Meaningful metric, no collapse',bad:'Weight clipping artifacts',use:'Stable training',c:'#fbbf24'},
            {name:'WGAN-GP',year:'2017',loss:'W + grad penalty',good:'Best stability, no clipping',bad:'Slower per step',use:'Most stable option',c:'#4ade80'},
            {name:'StyleGAN 2/3',year:'2019-21',loss:'Non-sat. + R1',good:'SOTA quality, style mixing',bad:'Needs massive compute',use:'Photorealistic faces',c:'#22d3ee'},
            {name:'CycleGAN',year:'2017',loss:'L1 + adv',good:'Unpaired image translation',bad:'Geometry/texture artifacts',use:'Photo↔painting, horse↔zebra',c:'#a78bfa'},
          ].map(m=>(
            <Panel key={m.name} title={`${m.name} (${m.year})`} accent={m.c}>
              {[['Loss',m.loss],['Pro',m.good],['Con',m.bad],['Use case',m.use]].map(([k,v])=>(
                <div key={k} style={{marginBottom:'4px',fontSize:'9px'}}><span style={{color:'var(--t3)',marginRight:6}}>{k}:</span><span style={{color:'var(--t2)'}}>{v}</span></div>
              ))}
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
